import React from 'react';

export interface Note {
  id?: string | number;
  content: string;
  createdAt?: string;
  isEditing?: boolean;
}

interface EditableNoteCardProps {
  note: Note;
  index: number;
  onSave: (index: number, updatedData: Note) => void;
  onCancel: (index: number) => void;
  onEdit: (index: number) => void;
}

const API_URL = process.env.REACT_APP_API_URL;

export const EditableNoteCard: React.FC<EditableNoteCardProps> = ({ 
  note, 
  index, 
  onSave, 
  onCancel, 
  onEdit 
}) => {
  const [editContent, setEditContent] = React.useState(note.content);

  const handleSave = () => {
    const updatedNote = {
      ...note,
      content: editContent,
      isEditing: false
    };
    onSave(index, updatedNote);
  };

  const formattedDate = note.createdAt 
    ? new Date(note.createdAt).toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : 'Just now';

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h4 className="font-semibold text-amber-600">Note</h4>
          <div className="flex space-x-2">
            <span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full">Note</span>
            {!note.isEditing && (
              <button
                onClick={() => onEdit(index)}
                className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs px-2 py-1 rounded"
              >
                Edit
              </button>
            )}
          </div>
        </div>
        
        {note.isEditing ? (
          <div className="space-y-3">
            <div>
              <label className="block text-gray-600 text-sm mb-1">Note Content</label>
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full p-2 border rounded text-sm h-24"
                placeholder="Enter your note content..."
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                className="bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1 rounded"
              >
                Save
              </button>
              <button
                onClick={() => onCancel(index)}
                className="bg-gray-500 hover:bg-gray-600 text-white text-xs px-3 py-1 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex flex-col">
              <span className="text-gray-600 mb-1">Content:</span>
              <span className="font-medium text-gray-800 text-xs bg-gray-50 p-2 rounded border min-h-[60px]">
                {note.content}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Created:</span>
              <span className="font-medium text-gray-500 text-xs">{formattedDate}</span>
            </div>

            {typeof note.id === 'string' && note.id.startsWith('temp-') && (
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className="font-medium text-blue-500 text-xs">Saving...</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EditableNoteCard;